import { useState, useRef, useEffect, useContext } from "react";
import { createPortal } from "react-dom";
import { Eye, EyeOff, X, Info } from "lucide-react";
import RegisterImage from "../assets/images/register.jpg";
import LoginImage from "../assets/images/login.jpg";
import TermsAndConditions from "./TermsAndConditions";
import HostTermsAndConditions from "./TermsAndConditionsHost";
import axios from "axios";
import Cookies from "js-cookie";
import { UserContext } from "./UserContext";

// Type definitions for Google OAuth
interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

interface GoogleAccounts {
  id: {
    initialize: (config: {
      client_id: string;
      callback: (response: GoogleCredentialResponse) => void;
    }) => void;
    prompt: () => void;
  };
}

interface OAuthUserData {
  email: string;
  given_name?: string;
  firstName?: string;
  family_name?: string;
  lastName?: string;
  sub?: string;
  id?: string;
  picture?: string;
  profilePictureUrl?: string;
}

// Add Google type declarations
declare global {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface Window {
    // @ts-expect-error - google may be defined elsewhere with different modifiers
    google?: {
      accounts: GoogleAccounts;
    };
  }
}

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const [isLogin, setIsLogin] = useState(true);
  const [forgotPasswordStep, setForgotPasswordStep] = useState<
    "email" | "verification" | "newPassword" | null
  >(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [agree, setAgree] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [tcConfirm, setTcConfirm] = useState(false);
  const [isHostTermsOpen, setIsHostTermsOpen] = useState(false);
  const [isHostInfoOpen, setIsHostInfoOpen] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [registerError, setRegisterError] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const { setUser } = useContext(UserContext);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleRegister = async (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault(); // Prevent the default form submission behavior
    setRegisterError(""); // Clear any previous errors

    // Validate inputs before making API call
    if (!firstName.trim() || !lastName.trim()) {
      setRegisterError("Please enter your first and last name");
      return;
    }

    if (!email.trim()) {
      setRegisterError("Please enter your email address");
      return;
    }

    if (!password || password.length < 6) {
      setRegisterError("Password must be at least 6 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setRegisterError("Passwords do not match");
      return;
    }

    if (!agree) {
      setRegisterError("Please agree to the Terms & Conditions");
      return;
    }

    if (isHost && !tcConfirm) {
      setRegisterError("Please agree to the hosting Terms & Conditions");
      return;
    }

    setIsRegistering(true); // Start loading state

    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        mobile: mobile.trim(),
        isHost,
        tc_Confirm: isHost ? tcConfirm : true, // Only required if isHost is true
      };
      const response = await axios.post("/api/account/create", payload);

      console.log("📝 Register Response:", response);
      console.log("📝 Response Data:", response.data);

      if (response.status === 200 && response.data.success) {
        // Registration successful
        setIsLogin(true);
        handleReset();
      } else if (response.data.message) {
        // Handle error response
        setRegisterError(response.data.message);
      }
    } catch (error: unknown) {
      console.error("Error submitting register form:", error);

      // Handle HTTP error responses (400, 500, etc.)
      if (
        axios.isAxiosError(error) &&
        error.response?.data?.message
      ) {
        setRegisterError(error.response.data.message);
      } else {
        setRegisterError(
          "An error occurred while registering. Please try again."
        );
      }
    } finally {
      setIsRegistering(false); // End loading state
    }
  };

  const handleLogin = async (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    setLoginError(false); // Clear previous errors

    // Validate inputs before making API call
    if (!email.trim() || !password) {
      setLoginError(true);
      return;
    }

    try {
      const payload = {
        email: email.trim(),
        password
      };
      const response = await axios.post("/api/account/login", payload);
      if (response.status === 200) {
        if (response.data.success) {
          Cookies.set("authToken", response.data.token, {
            expires: 7,
            secure: true, // HTTPS only
            sameSite: "strict", // CSRF protection
          });
          setUser(response.data); // Update the context state
          setLoginError(false);
          onClose();
        } else {
          setLoginError(true);
        }
      }
    } catch (error: unknown) {
      console.error("Error submitting login form:", error);
      setLoginError(true);
    }
  };

  const handleForgotPasswordEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotPasswordEmail.trim()) {
      setForgotPasswordError("Please enter your email address");
      return;
    }

    setIsSubmitting(true);
    setForgotPasswordError("");

    try {
      const response = await axios.post("/api/Account/forgotPassword", {
        email: forgotPasswordEmail,
      });

      if (response.status === 200 && response.data.success) {
        setForgotPasswordSuccess("Verification code sent to your email");
        setForgotPasswordStep("verification");
      } else {
        setForgotPasswordError(
          response.data.message || "Failed to send verification code"
        );
      }
    } catch (error: unknown) {
      console.error("Error sending forgot password email:", error);
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        setForgotPasswordError(error.response.data.message);
      } else {
        setForgotPasswordError("An error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerificationCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      setForgotPasswordError("Please enter the 6-digit verification code");
      return;
    }

    setIsSubmitting(true);
    setForgotPasswordError("");

    try {
      const response = await axios.post("/api/Account/verifyResetCode", {
        email: forgotPasswordEmail,
        code: verificationCode,
      });

      if (response.status === 200 && response.data.success) {
        setForgotPasswordSuccess("Code verified successfully");
        setForgotPasswordStep("newPassword");
      } else {
        setForgotPasswordError(
          response.data.message || "Invalid verification code"
        );
      }
    } catch (error: unknown) {
      console.error("Error verifying code:", error);
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        setForgotPasswordError(error.response.data.message);
      } else {
        setForgotPasswordError("An error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword.trim() || !confirmPassword.trim()) {
      setForgotPasswordError("Please enter both password fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setForgotPasswordError("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setForgotPasswordError("Password must be at least 6 characters long");
      return;
    }

    setIsSubmitting(true);
    setForgotPasswordError("");

    try {
      const response = await axios.post("/api/Account/resetPassword", {
        email: forgotPasswordEmail,
        code: verificationCode,
        newPassword: newPassword,
      });

      if (response.status === 200 && response.data.success) {
        setForgotPasswordSuccess(
          "Password reset successfully! You can now login with your new password."
        );
        setTimeout(() => {
          setForgotPasswordStep(null);
          setIsLogin(true);
          setForgotPasswordEmail("");
          setVerificationCode("");
          setNewPassword("");
          setConfirmPassword("");
          setForgotPasswordError("");
          setForgotPasswordSuccess("");
        }, 2000);
      } else {
        setForgotPasswordError(
          response.data.message || "Failed to reset password"
        );
      }
    } catch (error: unknown) {
      console.error("Error resetting password:", error);
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        setForgotPasswordError(error.response.data.message);
      } else {
        setForgotPasswordError("An error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset all form fields
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setMobile("");
    setAgree(false);

    // Reset all error states
    setLoginError(false);
    setRegisterError("");
    setForgotPasswordError("");
    setForgotPasswordSuccess("");

    // Reset forgot password flow
    setForgotPasswordStep(null);
    setForgotPasswordEmail("");
    setVerificationCode("");
    setNewPassword("");

    // Reset loading states
    setIsSubmitting(false);
    setIsRegistering(false);

    // Reset host states
    setIsHost(false);
    setTcConfirm(false);

    // Reset visibility states
    setShowPassword(false);
    setShowConfirmPassword(false);

    onClose();
  };

  const handleReset = () => {
    // Reset all form fields
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setMobile("");
    setAgree(false);

    // Reset error states
    setLoginError(false);
    setRegisterError("");

    // Reset loading state
    setIsRegistering(false);

    // Reset host states
    setIsHost(false);
    setTcConfirm(false);

    // Reset password visibility
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleOAuthLogin = async (provider: string, userData: OAuthUserData) => {
    try {
      const payload = {
        email: userData.email,
        firstName: userData.given_name || userData.firstName || "",
        lastName: userData.family_name || userData.lastName || "",
        provider: provider,
        providerId: userData.sub || userData.id || "",
        profilePictureUrl: userData.picture || userData.profilePictureUrl || "",
      };

      const response = await axios.post("/api/account/oauth-login", payload);

      if (response.status === 200 && response.data.success) {
        Cookies.set("authToken", response.data.token, {
          expires: 7,
          secure: true,
          sameSite: "strict",
        });
        setUser(response.data);
        onClose();
      } else {
        setLoginError(true);
        setRegisterError(response.data.message || "OAuth login failed");
      }
    } catch (error: unknown) {
      console.error("Error during OAuth login:", error);
      setLoginError(true);
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        setRegisterError(error.response.data.message);
      } else {
        setRegisterError(
          "An error occurred during OAuth login. Please try again."
        );
      }
    }
  };

  // @ts-expect-error - Reserved for future Google OAuth implementation
  const _handleGoogleLogin = () => {
    if (!window.google) {
      console.error("Google Sign-In not loaded");
      setRegisterError(
        "Google Sign-In is not available. Please try again later."
      );
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id:
          import.meta.env.VITE_GOOGLE_CLIENT_ID || "YOUR_GOOGLE_CLIENT_ID",
        callback: (response: GoogleCredentialResponse) => {
          try {
            // Validate credential exists
            if (!response.credential) {
              throw new Error("No credential received from Google");
            }

            // Decode the JWT credential to get user info
            const parts = response.credential.split(".");
            if (parts.length !== 3) {
              throw new Error("Invalid JWT format");
            }

            const userData = JSON.parse(atob(parts[1])) as OAuthUserData;

            // Validate required user data
            if (!userData.email) {
              throw new Error("Email not provided by Google");
            }

            handleOAuthLogin("Google", userData);
          } catch (error: unknown) {
            console.error("Error parsing Google response:", error);
            const errorMessage =
              error instanceof Error
                ? error.message
                : "Failed to process Google login";
            setRegisterError(`${errorMessage}. Please try again.`);
          }
        },
      });

      window.google.accounts.id.prompt();
    } catch (error: unknown) {
      console.error("Error initializing Google Sign-In:", error);
      setRegisterError("Failed to initialize Google Sign-In. Please try again.");
    }
  };

  // @ts-expect-error - Reserved for future Apple OAuth implementation
  const _handleAppleLogin = async () => {
    try {
      // Apple Sign In would require AppleID.auth.init() and AppleID.auth.signIn()
      // This is a placeholder implementation
      // You'll need to set up Apple Sign In on the Apple Developer Portal
      // and configure the credentials

      // For now, show a message that Apple Sign In needs configuration
      setRegisterError(
        "Apple Sign In is coming soon. Please use Google or email registration."
      );

      // Uncomment and configure when ready:
      /*
      if (typeof AppleID !== 'undefined') {
        const data = await AppleID.auth.signIn();
        const userData = {
          email: data.user.email,
          firstName: data.user.name?.firstName || "",
          lastName: data.user.name?.lastName || "",
          id: data.user.id
        };
        handleOAuthLogin("Apple", userData);
      }
      */
    } catch (error: unknown) {
      console.error("Error during Apple login:", error);
      setRegisterError(
        "An error occurred during Apple login. Please try again."
      );
    }
  };

  useEffect(() => {
    // Load Google Sign-In script
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;

    // Handle script loading errors
    script.onerror = () => {
      console.error("Failed to load Google Sign-In script");
    };

    document.body.appendChild(script);

    return () => {
      // Safely remove script if it exists
      try {
        if (script.parentNode === document.body) {
          document.body.removeChild(script);
        }
      } catch (error) {
        console.error("Error removing Google Sign-In script:", error);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-[70vw] max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-6 right-6 z-50 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all duration-200 shadow-lg"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>

          {forgotPasswordStep ? (
            /* Forgot Password Flow */
            <div className="flex h-full min-h-[500px] max-h-[90vh]">
              {/* Centered Form */}
              <div className="flex-1 p-8 py-12 flex flex-col justify-start max-w-md mx-auto overflow-y-auto">
                {/* Logo/Brand */}
                <div className="mb-8">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-sm">
                    <span className="text-2xl font-bold text-gray-800">AA</span>
                  </div>

                  {forgotPasswordStep === "email" && (
                    <>
                      <h2 className="text-3xl font-bold text-gray-800 mb-2">
                        Forgot Password
                      </h2>
                      <p className="text-gray-600">
                        Enter your email to receive a verification code
                      </p>
                    </>
                  )}

                  {forgotPasswordStep === "verification" && (
                    <>
                      <h2 className="text-3xl font-bold text-gray-800 mb-2">
                        Enter Verification Code
                      </h2>
                      <p className="text-gray-600">
                        Check your email for the 6-digit verification code
                      </p>
                    </>
                  )}

                  {forgotPasswordStep === "newPassword" && (
                    <>
                      <h2 className="text-3xl font-bold text-gray-800 mb-2">
                        Reset Password
                      </h2>
                      <p className="text-gray-600">Enter your new password</p>
                    </>
                  )}
                </div>

                {/* Email Step */}
                {forgotPasswordStep === "email" && (
                  <form
                    onSubmit={handleForgotPasswordEmail}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        className="w-full p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--primary-50-ring)] focus:border-[var(--primary-50)] transition-all placeholder-gray-500"
                        required
                      />
                    </div>

                    {/* Error/Success Messages */}
                    {forgotPasswordError && (
                      <div className="text-red-500 text-sm">
                        {forgotPasswordError}
                      </div>
                    )}
                    {forgotPasswordSuccess && (
                      <div className="text-green-600 text-sm">
                        {forgotPasswordSuccess}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-[var(--primary-50)] to-[var(--primary-50-ring)] text-[var(--primary-50-text)] hover:opacity-90 disabled:from-gray-400 disabled:to-gray-500 transition-all duration-200 rounded-xl py-4 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
                    >
                      {isSubmitting ? "Sending..." : "Send Verification Code"}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setForgotPasswordStep(null);
                        setForgotPasswordError("");
                        setForgotPasswordSuccess("");
                      }}
                      className="w-full text-[var(--primary-50-text)] hover:opacity-80 font-semibold hover:underline transition-colors"
                    >
                      Back to Login
                    </button>
                  </form>
                )}

                {/* Verification Code Step */}
                {forgotPasswordStep === "verification" && (
                  <form onSubmit={handleVerificationCode} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        6-Digit Verification Code
                      </label>
                      <input
                        type="text"
                        placeholder="000000"
                        value={verificationCode}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6);
                          setVerificationCode(value);
                        }}
                        className="w-full p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--primary-50-ring)] focus:border-[var(--primary-50)] transition-all placeholder-gray-500 text-center text-2xl tracking-widest font-mono"
                        maxLength={6}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter the 6-digit code sent to {forgotPasswordEmail}
                      </p>
                    </div>

                    {/* Error/Success Messages */}
                    {forgotPasswordError && (
                      <div className="text-red-500 text-sm">
                        {forgotPasswordError}
                      </div>
                    )}
                    {forgotPasswordSuccess && (
                      <div className="text-green-600 text-sm">
                        {forgotPasswordSuccess}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting || verificationCode.length !== 6}
                      className="w-full bg-gradient-to-r from-[var(--primary-50)] to-[var(--primary-50-ring)] text-[var(--primary-50-text)] hover:opacity-90 disabled:from-gray-400 disabled:to-gray-500 transition-all duration-200 rounded-xl py-4 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
                    >
                      {isSubmitting ? "Verifying..." : "Verify Code"}
                    </button>

                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => {
                          setForgotPasswordStep("email");
                          setVerificationCode("");
                          setForgotPasswordError("");
                          setForgotPasswordSuccess("");
                        }}
                        className="flex-1 text-[var(--primary-50-text)] hover:opacity-80 font-semibold hover:underline transition-colors"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={handleForgotPasswordEmail}
                        disabled={isSubmitting}
                        className="flex-1 text-[var(--secondary)] hover:text-blue-700 font-semibold hover:underline transition-colors disabled:text-gray-400"
                      >
                        Resend Code
                      </button>
                    </div>
                  </form>
                )}

                {/* New Password Step */}
                {forgotPasswordStep === "newPassword" && (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter new password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full p-4 pr-12 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--primary-50-ring)] focus:border-[var(--primary-50)] transition-all placeholder-gray-500"
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--primary-50-ring)] focus:border-[var(--primary-50)] transition-all placeholder-gray-500"
                        required
                        minLength={6}
                      />
                    </div>

                    {/* Error/Success Messages */}
                    {forgotPasswordError && (
                      <div className="text-red-500 text-sm">
                        {forgotPasswordError}
                      </div>
                    )}
                    {forgotPasswordSuccess && (
                      <div className="text-green-600 text-sm">
                        {forgotPasswordSuccess}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-[var(--primary-50)] to-[var(--primary-50-ring)] text-[var(--primary-50-text)] hover:opacity-90 disabled:from-gray-400 disabled:to-gray-500 transition-all duration-200 rounded-xl py-4 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
                    >
                      {isSubmitting ? "Resetting..." : "Reset Password"}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setForgotPasswordStep("verification");
                        setNewPassword("");
                        setConfirmPassword("");
                        setForgotPasswordError("");
                        setForgotPasswordSuccess("");
                      }}
                      className="w-full text-[var(--primary-50-text)] hover:opacity-80 font-semibold hover:underline transition-colors"
                    >
                      Back
                    </button>
                  </form>
                )}
              </div>
            </div>
          ) : isLogin ? (
            /* Login Layout - Image on left, Form on right */
            <div className="flex h-full min-h-[500px] max-h-[90vh]">
              {/* Left Panel - Image */}
              <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
                <div className="absolute inset-0 z-10"></div>
                <img
                  src={LoginImage}
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                />

                {/* Floating UI elements for visual interest */}
                <div className="absolute top-8 left-8 z-20">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg max-w-xs">
                    <div className="flex items-center gap-2 text-[var(--primary-50-text)] font-semibold text-sm mb-2">
                      <div className="w-2 h-2 bg-[var(--primary-50)] rounded-full"></div>
                      Task Review With Team
                    </div>
                    <div className="text-xs text-gray-600">09:30am-10:00am</div>
                  </div>
                </div>

                <div className="absolute bottom-8 left-8 z-20">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
                    <div className="text-sm font-semibold text-gray-800 mb-2">
                      Daily Meeting
                    </div>
                    <div className="text-xs text-gray-600 mb-3">
                      12:00pm-01:00pm
                    </div>
                    <div className="flex -space-x-2">
                      <div className="w-6 h-6 bg-gray-400 rounded-full border-2 border-white"></div>
                      <div className="w-6 h-6 bg-blue-400 rounded-full border-2 border-white"></div>
                      <div className="w-6 h-6 bg-green-400 rounded-full border-2 border-white"></div>
                      <div className="w-6 h-6 bg-purple-400 rounded-full border-2 border-white"></div>
                    </div>
                  </div>
                </div>

                <div className="absolute top-1/2 left-12 z-20">
                  <div className="flex flex-col -space-y-1">
                    <div className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full border-2 border-white shadow-lg"></div>
                    <div className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full border-2 border-white shadow-lg ml-4"></div>
                    <div className="w-6 h-6 bg-white/90 backdrop-blur-sm rounded-full border-2 border-white shadow-lg ml-6"></div>
                  </div>
                </div>
              </div>

              {/* Right Panel - Form */}
              <div className="flex-1 p-8 py-12 flex flex-col justify-start max-w-md mx-auto overflow-y-auto">
                {/* Logo/Brand */}
                <div className="mb-6">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-sm">
                    <span className="text-2xl font-bold text-gray-800">AA</span>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-2">
                    Welcome back
                  </h2>
                  <p className="text-gray-600">Sign in to your account</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="amelielaurent7622@gmail.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--primary-50-ring)] focus:border-[var(--primary-50)] transition-all placeholder-gray-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••••••••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full p-4 pr-12 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--primary-50-ring)] focus:border-[var(--primary-50)] transition-all placeholder-gray-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Error message */}
                  {loginError && (
                    <div className="flex justify-between items-center">
                      <span className="text-red-500 text-sm">
                        Invalid Email / Password
                      </span>
                      <button
                        type="button"
                        className="text-[var(--secondary)] hover:text-blue-800 text-sm underline transition-colors"
                        onClick={() => {
                          setLoginError(false);
                          setForgotPasswordStep("email");
                        }}
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}

                  <button
                    onClick={handleLogin}
                    className="w-full bg-gradient-to-r from-[var(--primary-50)] to-[var(--primary-50-ring)] text-[var(--primary-50-text)] hover:opacity-90 transition-all duration-200 rounded-xl py-4 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    Sign In
                  </button>
                </div>

                {/* Social buttons */}
                {/*<div className="mt-6">*/}
                {/*  <div className="relative mb-4">*/}
                {/*    <div className="absolute inset-0 flex items-center">*/}
                {/*      <div className="w-full border-t border-gray-300/50"></div>*/}
                {/*    </div>*/}
                {/*    <div className="relative flex justify-center text-sm">*/}
                {/*      <span className="bg-gradient-to-br from-[var(--primary)] via-[var(--primary-25)] to-[var(--primary-50)] px-4 text-gray-500">*/}
                {/*        Or sign in with*/}
                {/*      </span>*/}
                {/*    </div>*/}
                {/*  </div>*/}

                {/*  <div className="grid grid-cols-2 gap-3">*/}
                {/*    <button*/}
                {/*      type="button"*/}
                {/*      onClick={handleGoogleLogin}*/}
                {/*      className="flex items-center justify-center gap-3 px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-300 rounded-xl hover:bg-white/90 hover:shadow-md transition-all text-gray-700 font-medium"*/}
                {/*    >*/}
                {/*      <svg className="w-5 h-5" viewBox="0 0 24 24">*/}
                {/*        <path*/}
                {/*          fill="#4285F4"*/}
                {/*          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"*/}
                {/*        />*/}
                {/*        <path*/}
                {/*          fill="#34A853"*/}
                {/*          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"*/}
                {/*        />*/}
                {/*        <path*/}
                {/*          fill="#FBBC05"*/}
                {/*          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"*/}
                {/*        />*/}
                {/*        <path*/}
                {/*          fill="#EA4335"*/}
                {/*          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"*/}
                {/*        />*/}
                {/*      </svg>*/}
                {/*      Google*/}
                {/*    </button>*/}
                {/*    <button*/}
                {/*      type="button"*/}
                {/*      onClick={handleAppleLogin}*/}
                {/*      className="flex items-center justify-center gap-3 px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-300 rounded-xl hover:bg-white/90 hover:shadow-md transition-all text-gray-700 font-medium"*/}
                {/*    >*/}
                {/*      <svg*/}
                {/*        className="w-5 h-5"*/}
                {/*        viewBox="0 0 24 24"*/}
                {/*        fill="currentColor"*/}
                {/*      >*/}
                {/*        <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />*/}
                {/*      </svg>*/}
                {/*      Apple*/}
                {/*    </button>*/}
                {/*  </div>*/}
                {/*</div>*/}

                {/* Toggle login/register */}
                <p className="mt-6 text-center text-gray-600 flex-shrink-0">
                  Don't have an account?{" "}
                  <button
                    type="button"
                    onClick={() => setIsLogin(!isLogin)}
                    className="text-[var(--primary-50-text)] hover:opacity-80 font-semibold hover:underline transition-colors"
                  >
                    Sign up
                  </button>
                </p>
              </div>
            </div>
          ) : (
            /* Register Layout - Form on left, Image on right */
            <div className="flex h-full min-h-[500px] max-h-[90vh]">
              {/* Left Panel - Form */}
              <div className="flex-1 p-8 py-12 flex flex-col justify-start max-w-md mx-auto overflow-y-auto">
                {/* Logo/Brand */}
                <div className="mb-6 flex-shrink-0">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-sm">
                    <span className="text-2xl font-bold text-gray-800">AA</span>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-2">
                    Create an account
                  </h2>
                  <p className="text-gray-600">Sign up now!</p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First name
                      </label>
                      <input
                        type="text"
                        placeholder="Amélie"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--primary-50-ring)] focus:border-[var(--primary-50)] transition-all placeholder-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last name
                      </label>
                      <input
                        type="text"
                        placeholder="Laurent"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--primary-50-ring)] focus:border-[var(--primary-50)] transition-all placeholder-gray-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="amelielaurent7622@gmail.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--primary-50-ring)] focus:border-[var(--primary-50)] transition-all placeholder-gray-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile
                    </label>
                    <input
                      type="mobile"
                      placeholder="+61 412 269 123"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      className="w-full p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--primary-50-ring)] focus:border-[var(--primary-50)] transition-all placeholder-gray-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••••••••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full p-4 pr-12 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--primary-50-ring)] focus:border-[var(--primary-50)] transition-all placeholder-gray-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="••••••••••••••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full p-4 pr-12 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--primary-50-ring)] focus:border-[var(--primary-50)] transition-all placeholder-gray-500"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {password &&
                      confirmPassword &&
                      password !== confirmPassword && (
                        <p className="mt-2 text-sm text-red-500">
                          Passwords do not match
                        </p>
                      )}
                  </div>

                  {/* IsHost Checkbox */}
                  <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isHost}
                      onChange={(e) => {
                        setIsHost(e.target.checked);
                        if (!e.target.checked) {
                          setTcConfirm(false); // Reset T&C if unchecking IsHost
                        }
                      }}
                      className="mt-0.5 w-4 h-4 rounded border-2 border-gray-300 text-[var(--primary-50)] focus:ring-[var(--primary-50-ring)] focus:ring-2"
                    />
                    <span className="flex items-center gap-2">
                      I want to host my workspace
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsHostInfoOpen(true);
                        }}
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                        title="Learn more about hosting"
                      >
                        <Info className="w-4 h-4" />
                      </button>
                    </span>
                  </label>

                  {/* Host Terms & Conditions - Only show if isHost is checked */}
                  {isHost && (
                    <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tcConfirm}
                        onChange={(e) => setTcConfirm(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-2 border-gray-300 text-[var(--primary-50)] focus:ring-[var(--primary-50-ring)] focus:ring-2"
                      />
                      <span>
                        I agree to the hosting{" "}
                        <button
                          type="button"
                          onClick={() => setIsHostTermsOpen(true)}
                          className="text-[var(--primary-50-text)] hover:underline font-medium"
                        >
                          Terms & Conditions
                        </button>
                      </span>
                    </label>
                  )}

                  {/* General Terms & Conditions */}
                  <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agree}
                      onChange={(e) => setAgree(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-2 border-gray-300 text-[var(--primary-50)] focus:ring-[var(--primary-50-ring)] focus:ring-2"
                    />
                    <span>
                      I agree to the{" "}
                      <button
                        type="button"
                        onClick={() => setIsTermsOpen(true)}
                        className="text-[var(--primary-50-text)] hover:underline font-medium"
                      >
                        Terms & Conditions
                      </button>
                    </span>
                  </label>

                  {/* Registration Error message */}
                  {registerError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <span className="text-red-600 text-sm font-medium">
                        {registerError}
                      </span>
                    </div>
                  )}

                  <button
                    onClick={handleRegister}
                    disabled={
                      isRegistering ||
                      password !== confirmPassword ||
                      !password ||
                      !confirmPassword
                    }
                    className="w-full bg-gradient-to-r from-[var(--primary-50)] to-[var(--primary-50-ring)] text-[var(--primary-50-text)] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 rounded-xl py-4 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:shadow-lg"
                  >
                    {isRegistering ? "Creating Account..." : "Submit"}
                  </button>
                </div>

                {/* Social buttons */}
                {/*<div className="mt-6">*/}
                {/*  <div className="relative mb-4">*/}
                {/*    <div className="absolute inset-0 flex items-center">*/}
                {/*      <div className="w-full border-t border-gray-300/50"></div>*/}
                {/*    </div>*/}
                {/*    <div className="relative flex justify-center text-sm">*/}
                {/*      <span className="bg-gradient-to-br from-[var(--primary)] via-[var(--primary-25)] to-[var(--primary-50)] px-4 text-gray-500">*/}
                {/*        Or register with*/}
                {/*      </span>*/}
                {/*    </div>*/}
                {/*  </div>*/}

                {/*  <div className="grid grid-cols-2 gap-3">*/}
                {/*    <button*/}
                {/*      type="button"*/}
                {/*      onClick={handleGoogleLogin}*/}
                {/*      className="flex items-center justify-center gap-3 px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-300 rounded-xl hover:bg-white/90 hover:shadow-md transition-all text-gray-700 font-medium"*/}
                {/*    >*/}
                {/*      <svg className="w-5 h-5" viewBox="0 0 24 24">*/}
                {/*        <path*/}
                {/*          fill="#4285F4"*/}
                {/*          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"*/}
                {/*        />*/}
                {/*        <path*/}
                {/*          fill="#34A853"*/}
                {/*          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"*/}
                {/*        />*/}
                {/*        <path*/}
                {/*          fill="#FBBC05"*/}
                {/*          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"*/}
                {/*        />*/}
                {/*        <path*/}
                {/*          fill="#EA4335"*/}
                {/*          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"*/}
                {/*        />*/}
                {/*      </svg>*/}
                {/*      Google*/}
                {/*    </button>*/}
                {/*    <button*/}
                {/*      type="button"*/}
                {/*      onClick={handleAppleLogin}*/}
                {/*      className="flex items-center justify-center gap-3 px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-300 rounded-xl hover:bg-white/90 hover:shadow-md transition-all text-gray-700 font-medium"*/}
                {/*    >*/}
                {/*      <svg*/}
                {/*        className="w-5 h-5"*/}
                {/*        viewBox="0 0 24 24"*/}
                {/*        fill="currentColor"*/}
                {/*      >*/}
                {/*        <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />*/}
                {/*      </svg>*/}
                {/*      Apple*/}
                {/*    </button>*/}
                {/*  </div>*/}
                {/*</div>*/}

                {/* Toggle login/register */}
                <p className="mt-6 text-center text-gray-600 flex-shrink-0">
                  Have an account?{" "}
                  <button
                    type="button"
                    onClick={() => setIsLogin(!isLogin)}
                    className="text-[var(--primary-50-text)] hover:opacity-80 font-semibold hover:underline transition-colors"
                  >
                    Sign in
                  </button>
                </p>
              </div>

              {/* Right Panel - Image */}
              <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
                <div className="absolute inset-0 z-10"></div>
                <img
                  src={RegisterImage}
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                />

                {/* Message Bubbles for visual interest */}
                <div className="absolute top-8 right-8 z-20">
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg max-w-xs">
                    <div className="text-sm font-medium text-gray-800">
                      Find a new workspace within minutes!
                    </div>
                  </div>
                </div>

                <div className="absolute top-1/2 left-8 z-20">
                  <div className="bg-[var(--primary-50)] backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg max-w-xs">
                    <div className="text-sm font-medium text-[var(--primary-50-text)]">
                      Explore your options
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-8 right-8 z-20">
                  <div className="bg-[var(--primary-25)]/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg max-w-xs">
                    <div className="text-sm font-medium text-[var(--primary-25-text)]">
                      Register, book and move in!
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Terms and Conditions Modal - Using Portal to render outside */}
      {isTermsOpen &&
        createPortal(
          <TermsAndConditions
            isOpen={isTermsOpen}
            onClose={() => setIsTermsOpen(false)}
          />,
          document.body
        )}

      {/* Host Info Modal */}
      {isHostInfoOpen &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
            onMouseDown={(e) => {
              e.stopPropagation();
              setIsHostInfoOpen(false);
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsHostInfoOpen(false);
                  }}
                  className="absolute top-6 right-6 z-10 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-200"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Info className="w-6 h-6 text-[var(--secondary)]" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    About Hosting Your Workspace
                  </h2>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  When you check <strong>"I want to host my workspace"</strong>,
                  you're indicating that you plan to list and manage workspaces
                  on our platform.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This will customize your experience to include:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>
                    <strong>Host Dashboard:</strong> Access to workspace
                    management tools
                  </li>
                  <li>
                    <strong>Booking Management:</strong> View and confirm tenant
                    bookings
                  </li>
                  <li>
                    <strong>Listing Tools:</strong> Create and manage your
                    workspace listings
                  </li>
                  <li>
                    <strong>Revenue Tracking:</strong> Monitor your earnings and
                    payouts
                  </li>
                </ul>
                <p className="text-gray-600 text-sm italic">
                  Note: You'll still have access to all consumer features like
                  browsing and booking other workspaces. This simply enhances
                  your account with hosting capabilities for a smoother
                  experience.
                </p>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsHostInfoOpen(false);
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-[var(--secondary)] text-white hover:opacity-90 transition-all duration-200 rounded-xl py-3 font-semibold shadow-lg"
                >
                  Got it, thanks!
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Host Terms and Conditions Modal - Using Portal to render outside */}
      {isHostTermsOpen &&
        createPortal(
          <HostTermsAndConditions
            isOpen={isHostTermsOpen}
            onClose={() => setIsHostTermsOpen(false)}
          />,
          document.body
        )}
    </>
  );
}
